import './style.css'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/utils/tailwind'

interface Option {
  id: string
  textKey: string
  value: string
}

export interface SelectProps {
  id: string
  textKey?: string
  value?: Option['value']
  options: Option[]
  onChange?: (value: Option) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  tabIndex?: number
  className?: string
  errorHidden?: boolean
}

export const Select = ({
  id,
  textKey,
  value,
  options,
  onChange = () => {},
  required,
  placeholder,
  disabled,
  error,
  tabIndex,
  className,
  errorHidden
}: SelectProps) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<Option | null>(() => options.find((option) => option.value === value) || null)

  useEffect(() => {
    if (!selected) return
    onChange(selected)
  }, [selected])

  useEffect(() => {
    // If no placeholder and no value, select first option (as default)
    if (!placeholder && !value) return onChange(options[0])
  }, [placeholder])

  return (
    <div className={cn('flex w-full flex-col', className)} data-testid={`${id}-container`}>
      {textKey && (
        <label data-testid={`${id}-label`} htmlFor={id} className="font-bold">
          {t(textKey)}
          {required && <span> *</span>}
        </label>
      )}

      <select
        id={id}
        name={id}
        data-testid={`${id}-select`}
        tabIndex={tabIndex}
        disabled={disabled}
        value={value || selected?.value}
        required={required}
        onChange={(e) => {
          setSelected(options.find((option) => option.value === e.target.value) || null)
        }}
        className={`w-full rounded-lg border p-4 ${
          error ? 'border-error' : 'border-gray focus-visible:border-primary focus-visible:outline-none'
        } disabled:bg-gray/50 bg-white disabled:cursor-not-allowed`}
      >
        {placeholder && (
          <option value="" disabled>
            {t(placeholder)}
          </option>
        )}

        {options.map((option) => (
          <option id={option.id} key={option.id} value={option.value}>
            {t(option.textKey)}
          </option>
        ))}
      </select>

      {!errorHidden && (
        <span className="text-error min-h-[1.5rem] text-sm" data-testid={`${id}-error`}>
          {error && t(error)}
        </span>
      )}
    </div>
  )
}
